import { defineMessages } from "react-intl";

import TourDescriptor from "@app/component/flows/types/TourDescriptor";

const messages = defineMessages({
  step01: {
    id: "guides.resources.operationsTour.step01",
    defaultMessage: "Resources",
  },
  step01Description: {
    id: "guides.resources.operationsTour.step01.description",
    defaultMessage:
      "There are multiple things you can do with resources. Let's review them one by one.",
  },
  step02: {
    id: "guides.resources.operationsTour.step02",
    defaultMessage: "Pinning",
  },
  step02Description: {
    id: "guides.resources.operationsTour.step02.description",
    defaultMessage: "Click here to pin or unpin a resource for easy access on mobile.",
  },
  step03: {
    id: "guides.resources.operationsTour.step03",
    defaultMessage: "Viewing",
  },
  step03Description: {
    id: "guides.resources.operationsTour.step03.description",
    defaultMessage: "You can click a resource row in order to open it. Let's click here.",
  },
  step04: {
    id: "guides.resources.operationsTour.step04",
    defaultMessage: "Downloading",
  },
  step04Description: {
    id: "guides.resources.operationsTour.step04.description",
    defaultMessage: "You can either download file from the preview page clicking here.",
  },
  step05: {
    id: "guides.resources.operationsTour.step05",
    defaultMessage: "Downloading",
  },
  step05Description: {
    id: "guides.resources.operationsTour.step05.description",
    defaultMessage:
      "Or by choosing a corresponding menu item. Let's click on Resource's triple dot menu to proceed.",
  },
  step06: {
    id: "guides.resources.operationsTour.step06",
    defaultMessage: "Downloading",
  },
  step06Description: {
    id: "guides.resources.operationsTour.step06.description",
    defaultMessage: "Then you can click <b>Download</b> to download resource on your computer.",
  },
  step07: {
    id: "guides.resources.operationsTour.step07",
    defaultMessage: "Sharing a Resource",
  },
  step07Description: {
    id: "guides.resources.operationsTour.step07.description",
    defaultMessage:
      "In order to share a resource, you can open the same menu and click <b>Share</b>...",
  },
  step08: {
    id: "guides.resources.operationsTour.step08",
    defaultMessage: "Sharing a Resource",
  },
  step08Description: {
    id: "guides.resources.operationsTour.step08.description",
    defaultMessage: "...or you can select resources (let's select all), and...",
  },
  step09: {
    id: "guides.resources.operationsTour.step09",
    defaultMessage: "Sharing a Resource",
  },
  step09Description: {
    id: "guides.resources.operationsTour.step09.description",
    defaultMessage: "Click <b>Share</b>.",
  },
  step10: {
    id: "guides.resources.operationsTour.step10",
    defaultMessage: "Sharing a Resource",
  },
  step10Description: {
    id: "guides.resources.operationsTour.step10.description",
    defaultMessage:
      "Select the Teams or Users you want to share the resource with. Click Share to confirm.",
  },
  step11: {
    id: "guides.resources.operationsTour.step11",
    defaultMessage: "Deleting a Resource",
  },
  step11Description: {
    id: "guides.resources.operationsTour.step11.description",
    defaultMessage:
      "To Delete a Resource, you can select one or multiple resources and then click <b>Delete</b> here. Confirm the deletion when prompted.",
  },
  step12: {
    id: "guides.resources.operationsTour.step12",
    defaultMessage: "Deleting a Resource",
  },
  step12Description: {
    id: "guides.resources.operationsTour.step12.description",
    defaultMessage:
      "Or you can delete from the menu. Let's click on Resource's triple dot menu to proceed.",
  },
  step13: {
    id: "guides.resources.operationsTour.step13",
    defaultMessage: "Deleting a Resource",
  },
  step13Description: {
    id: "guides.resources.operationsTour.step13.description",
    defaultMessage: "Click <b>Delete</b>.",
  },
  step14: {
    id: "guides.resources.operationsTour.step14",
    defaultMessage: "Tour Complete",
  },
  step14Description: {
    id: "guides.resources.operationsTour.step14.description",
    defaultMessage: "🎉 Congratulations! You've completed this tour.",
  },
});

export const operationsTourConfiguration: TourDescriptor = {
  id: "mmc-resources-operations",
  steps: [
    {
      id: "intro",
      description: messages.step01Description,
      title: messages.step01,
    },
    {
      id: "resource-pin",
      description: messages.step02Description,
      selector: "[data-action-id=resource-item-pin-unpin]",
      title: messages.step02,
    },
    {
      id: "resource-open",
      description: messages.step03Description,
      options: {
        disableInteraction: false,
        expectRouteChange: true,
        showBackground: true,
        showButtons: false,
      },
      selector: "[class*=ResourceList_list] .mmc-js-resource-file [class*=ResourceInfo_name]",
      title: messages.step03,
      waitForEvent: {
        eventType: "click",
        selector: "[class*=ResourceList_list] .mmc-js-resource-file [class*=ResourceInfo_name]",
      },
    },
    {
      id: "downloading-1",
      description: messages.step04Description,
      goToPathAfter: "/resources",
      position: "bottom",
      selector: "[data-action-id=file-download]",
      title: messages.step04,
    },
    {
      id: "downloading-2",
      description: messages.step05Description,
      options: {
        disableInteraction: false,
        showBackground: true,
        showButtons: false,
      },
      position: "bottom",
      selector:
        "[class*=ResourceList_list] .mmc-js-resource-file [data-action-id=resource-item-more]",
      title: messages.step05,
      waitForEvent: {
        eventType: "click",
        selector:
          "[class*=ResourceList_list] .mmc-js-resource-file [data-action-id=resource-item-more]",
      },
    },
    {
      id: "downloading-3",
      description: messages.step06Description,
      position: "left",
      selector: "[data-menu-id$=download]",
      title: messages.step06,
    },
    {
      id: "share-1",
      description: messages.step07Description,
      onAfter: async () => {
        // clicking "elsewhere" to close the menu
        const element = document.querySelector("[class*=ResourceList_title]");
        if (element) {
          (element as HTMLElement).click();
        }
      },
      position: "left",
      selector: "[data-menu-id$=share]",
      title: messages.step07,
    },
    {
      id: "share-2",
      description: messages.step08Description,
      options: {
        disableInteraction: false,
        showBackground: true,
        showButtons: false,
      },
      position: "bottom",
      selector: "[data-action-id=resource-select-deselect-all]",
      title: messages.step08,
      waitForEvent: {
        eventType: "click",
        selector: "[data-action-id=resource-select-deselect-all]",
      },
    },
    {
      id: "share-3",
      description: messages.step09Description,
      options: {
        disableInteraction: false,
        showBackground: true,
        showButtons: false,
      },
      position: "bottom",
      selector: "[data-action-id=resources-bulk-share]",
      title: messages.step09,
      waitForEvent: {
        eventType: "click",
        selector: "[data-action-id=resources-bulk-share]",
      },
    },
    {
      id: "share-4",
      description: messages.step10Description,
      onAfter: async () => {
        // close sharing modal
        const element = document.querySelector("[class*=ShareModal_container] .ant-modal-close");
        if (element) {
          (element as HTMLElement).click();
        }
      },
      title: messages.step10,
    },
    {
      id: "deleting-1",
      description: messages.step11Description,
      onAfter: async () => {
        // deselect records
        const element = document.querySelector("[data-action-id=resource-select-deselect-all]");
        if (element) {
          (element as HTMLElement).click();
        }
      },
      position: "bottom",
      selector: "[data-action-id=resources-bulk-delete]",
      title: messages.step11,
    },
    {
      id: "deleting-2",
      description: messages.step12Description,
      options: {
        disableInteraction: false,
        showBackground: false,
        showButtons: false,
      },
      selector: "[class*=ResourceList_list] [data-action-id=resource-item-more]",
      title: messages.step12,
      waitForEvent: {
        eventType: "click",
        selector: "[class*=ResourceList_list] [data-action-id=resource-item-more]",
      },
    },
    {
      id: "deleting-3",
      description: messages.step13Description,
      onAfter: async () => {
        // clicking "elsewhere" to close the menu
        const element = document.querySelector("[class*=ResourceList_title]");
        if (element) {
          (element as HTMLElement).click();
        }
      },
      selector: "[data-menu-id$=delete]",
      title: messages.step13,
    },
    {
      id: "completed",
      description: messages.step14Description,
      title: messages.step14,
    },
  ],
};
